import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ConfigurationsService, LocalesService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { JobsService, ModalComponent, ProfileService, StoreService, TrailService } from 'core/index';
import { KeplerService } from 'app/kepler/services/kepler.service';
import { saveAs } from 'file-saver';
import { lastValueFrom, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { KeplerMatch } from '../../models/KeplerMatch';
import { Decorator, DynamicFormComponent, EntityQuestion, QuestionBase, TextboxQuestion } from 'core/components';
import { LeagueService } from 'core/services/league.service';
@Component({
  selector: 'lms-kepler-ranking-list',
  templateUrl: './kepler-ranking-list.component.html',
  styleUrls: ['./kepler-ranking-list.component.scss']
})
export class KeplerRankingListComponent implements OnInit, OnDestroy {
  @ViewChild('federationsRanking', { static: false }) federationsRankingTable: ElementRef;
  @ViewChild('groupRanking', { static: false }) groupRankingTable: ElementRef;
  @ViewChild('editLeague', { static: false }) public editLeague: ModalComponent;
  @ViewChild('addFederations', { static: false }) public addFederations: ModalComponent;
  @ViewChild(DynamicFormComponent, { static: false }) public dynamicForm: DynamicFormComponent;
  @Input() options: any;
  @Input() match: KeplerMatch;
  public user: any;
  public keplerRankingGroupListId = 'KEPLER_RANKING_GROUP_LIST';
  public keplerRankingFederationListId = 'KEPLER_RANKING_FEDERATION_LIST';
  public toolbarConfig: any;
  public view: string = 'groups';
  public jobSubscription: Subscription = new Subscription();
  public downloadSubscription: Subscription = new Subscription();
  public dataSource: any;
  public groupFilterSelected: any;
  public groupFilterOptions = [];
  public federationFilterSelected: any;
  public federationFilterOptions = [];
  public tooltipRankingFilterText: any;
  public nameQuestions: QuestionBase<any>[] = [];
  public leagueQuestions: QuestionBase<any>[] = [];
  public isAnyFederationSelected = false;
  public selectedFederationRankings = [];
  constructor(
    private configurationsService: ConfigurationsService,
    private localesService: LocalesService,
    private leagueService: LeagueService,
    public translateService: TranslateService,
    public profileService: ProfileService,
    public trailService: TrailService,
    public keplerService: KeplerService,
    public jobsService: JobsService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.profileService.getLoggedUser$().subscribe((loggedUser) => {
      this.user = loggedUser;
    });
    this.toolbarConfig = {
      list: {
        listId: this.view === 'groups' ? this.keplerRankingGroupListId : this.keplerRankingFederationListId,
        download: false,
        filterRow: true,
        columnChooser: false,
        skeletor: true,
        customDownload: true
      }
    };
    this.setFiltersOptions();
    this.tooltipRankingFilterText = await this.getInfoText('kepler.ranking.filter.info');
  }

  ngOnDestroy() {
    this.jobSubscription.unsubscribe();
    this.downloadSubscription.unsubscribe();
  }

  selectRanking(option: string) {
    switch (option) {
      case 'groups':
        this.view = 'groups';
        this.toolbarConfig.list.listId = this.keplerRankingGroupListId;
        break;
      case 'federations':
        this.view = 'federations';
        this.toolbarConfig.list.listId = this.keplerRankingFederationListId;
        break;
    }
  }

  setFiltersOptions() {
    this.groupFilterSelected = 'all';
    this.federationFilterSelected = 'all';
    this.options.filters.group = 'all';
    this.options.filters.league = 'all';
    this.federationFilterOptions = [
      {
        key: 'all',
        value: this.translateService.instant('kepler.league.allFederations')
      }
    ];
    this.groupFilterOptions = [{ key: 'all', value: this.translateService.instant('kepler.ranking.all') }];

    this.match.groups?.forEach((group) => {
      this.groupFilterOptions.push({ key: group, value: group });
    });
    this.match.leagues?.forEach((league) => {
      this.federationFilterOptions.push({ key: league.name, value: league.name });
    });
  }

  downloadReport(all?: boolean) {
    this.cdr.detectChanges();
    const { filters, page, limit, sort, search, aggregates, populate, $filter } = (
      this.view === 'groups' ? (this.groupRankingTable as any) : (this.federationsRankingTable as any)
    ).dataSource;
    const paginationOptions = all
      ? { sort, aggregates, populate, export: 'ALL' }
      : { page, limit, sort, search, aggregates, populate, $filter };
    if (this.view === 'groups') {
      this.keplerService
        .downloadRanking(this.options.filters.matchId, { ...paginationOptions, group: filters.group })
        .subscribe((res) => this.manageBulkJob(res));
    } else {
      this.downloadSubscription = this.keplerService
        .downloadFederationRanking(this.options.filters.matchId, {
          ...paginationOptions,
          league: filters.league == 'all' ? '' : filters.league
        })
        .subscribe((res) => this.manageBulkJob(res));
    }
  }

  manageBulkJob(job) {
    this.jobSubscription = this.jobsService
      .manageJob(
        job.jobId,
        this.view === 'groups'
          ? this.jobsService.jobTypes.DOWNLOAD_GROUP_RANKING
          : this.jobsService.jobTypes.DOWNLOAD_FEDERATIONS_RANKING
      )
      .subscribe((job) => {
        if (job.status === 'finished' || job.status === 'fatal-error') {
          this.jobSubscription.unsubscribe();
          this.downloadSubscription.unsubscribe();
          this.http.get(job.details.summary.fileUrl, { responseType: 'blob' }).subscribe((res) => {
            const { matchId, group, league } = this.options.filters;
            saveAs(
              res,
              this.view === 'groups'
                ? `KeplerGroupRanking_${group}_${matchId}.csv`
                : league
                ? `KeplerFederationsRanking_${league}_${matchId}.csv`
                : `KeplerFederationsRanking_${matchId}.csv`
            );
          });
        }
      });
  }

  onSelectorChange(event) {
    this.groupFilterSelected = event;
    this.options.filters.group = event;
    (this.groupRankingTable as any).dataSource.refreshData({ filters: this.options.filters, page: 1 });
    StoreService.update(this.keplerRankingGroupListId, (this.groupRankingTable as any).dataSource);
  }
  onFederationSelectorChange(event) {
    this.federationFilterSelected = event;
    this.options.filters.league = event;
    (this.federationsRankingTable as any).dataSource.refreshData({ filters: this.options.filters, page: 1 });
    StoreService.update(this.keplerRankingFederationListId, (this.federationsRankingTable as any).dataSource);
  }

  closeModal() {
    this.editLeague.close();
  }
  openModal() {
    this.editLeague.open();
    this.getNameQuestion();
  }
  openAddFederationModal() {
    this.addFederations.open();
    const haveLeague = this.selectedFederationRankings.some((x) => x.league);
    this.getLeagueQuestion(haveLeague);
  }
  closeFederationModal() {
    this.addFederations.close();
  }

  addFederation(value) {
    const body = {
      league: value.nameLeague,
      federations: this.selectedFederationRankings.map((x) => x.federation)
    };
    this.leagueService.editLeague(this.match.id, body).finally(() => {
      this.updateMatch();
    });
    this.closeFederationModal();
  }

  updateMatch() {
    const matchId = this.match.id;
    this.keplerService.getKeplerMatch$(matchId).subscribe((match) => {
      this.match = match;
      this.setFiltersOptions();
      this.onFederationSelectorChange('all');
    });
  }

  /**
   * Get the text from contentful/configurations
   * @param key
   * @private
   */
  private async getInfoText(key: string): Promise<any> {
    return lastValueFrom(
      this.configurationsService.getInfoMessage$(
        'toolTip',
        'fields.keys',
        key,
        'textoDeAyuda',
        this.localesService.getBrowserLocale(),
        true
      )
    );
  }

  private getNameQuestion() {
    this.nameQuestions = [
      new TextboxQuestion({
        key: 'nameLeague',
        label: 'nouns.name',
        class: 'col-1',
        placeholder: 'actions.writeHere',
        value: this.federationFilterSelected,
        validators: {
          required: true
        }
      })
    ];
  }

  private getLeagueQuestion(haveLeague) {
    this.leagueQuestions = [
      new Decorator({
        html: `<span class="glicon-alert-thin"></span><span>${this.translateService.instant(
          'kepler.league.alertFederationSelected'
        )}</span>`,
        valueClass: 'gl-forms__info no-decorator-margin',
        hidden: !haveLeague
      }),
      new EntityQuestion({
        key: 'nameLeague',
        label: 'kepler.league.name',
        class: 'col-1',
        value: null,
        isLocalEntity: true,
        localEntityData: { docs: this.match.leagues },
        entityOptions: {
          valueField: 'name',
          preload: '',
          freeEdit: true
        },
        validators: { required: true },
        order: 1
      })
    ];
  }
}
